// M004-F0612 / T-M004-0019 — shared dialog/modal outside-dismiss primitive.
// Design: docs/design/M004-D016-Dialog-and-Modal-Interaction-Standard.md §4.2 (studioaeye-Core).
// Mantis #1176.
//
// Owner report (paraphrased): selecting text inside a dialog and dragging the
// mouse outside the box was destroying in-progress edits, because the overlay
// closed on a bare `onClick`. A browser synthesizes `click` on the nearest
// common ancestor of a mousedown/mouseup pair even when the two targets
// differ, so patching the `click` handler alone cannot fix this — the fix has
// to look at where the press *started* as well as where it *ended*.
//
// Contract: the dialog dismisses only when both the `mousedown` and the
// matching `mouseup` target the overlay element itself
// (`event.target === event.currentTarget`). A press that starts inside the
// dialog and releases outside (or the symmetric outside-to-inside drag) never
// dismisses. Escape and an explicit close control are unaffected — they call
// the same dismiss handler directly and do not go through this hook.
//
// Usage: spread the returned props onto the overlay/backdrop element only.
// The inner dialog box needs no changes (existing `onClick={stopPropagation}`
// on the box, if present, is harmless and may be left as-is).
//
//   const overlayDismissHandlers = useDismissOnOutsideClick(closeModal);
//   <div className="modal-overlay" {...overlayDismissHandlers}>
//     <div className="modal">...</div>
//   </div>
//
// `onDismiss` may be omitted/falsy; the hook then no-ops instead of throwing,
// which matches call sites that conditionally allow dismissal.
import { useCallback, useRef } from 'react';

export default function useDismissOnOutsideClick(onDismiss) {
  const pressOriginatedOnOverlay = useRef(false);

  const handleOverlayMouseDown = useCallback((event) => {
    pressOriginatedOnOverlay.current = event.target === event.currentTarget;
  }, []);

  const handleOverlayMouseUp = useCallback((event) => {
    const shouldDismiss = pressOriginatedOnOverlay.current && event.target === event.currentTarget;
    // Reset unconditionally so a stray mouseup never leaves stale state for
    // the next gesture (see M004-D016 §4.2).
    pressOriginatedOnOverlay.current = false;
    if (shouldDismiss && typeof onDismiss === 'function') {
      onDismiss(event);
    }
  }, [onDismiss]);

  return { onMouseDown: handleOverlayMouseDown, onMouseUp: handleOverlayMouseUp };
}
